import { RichTreeView } from "@mui/x-tree-view";
import ADWFileTypeOption from "./ADWFileTypeOption";
import { useMemo, useRef } from "react";
import { useTreeViewApiRef } from '@mui/x-tree-view/hooks';
import { TreeViewBaseItem } from '@mui/x-tree-view/models';
import LassoToggleButtons from "../../../../components/LassoToggleButtons";
import { selectAdvancedFiles, adwUpdateSelectedFile, setSelectedFileGroups, selectSelectedFileGroups } from "../../dataWranglerSlice";
import { useSelector, useDispatch } from "react-redux";
import { setWithPath, getWithPath, isFileSelected } from "../../UtilityFunctions";
import { Stack } from "@mui/material";

interface Props {
    options: any;
    tabularFilter?: { selectionPath: string[] },
}

const FileTreeView = ({ options = [], tabularFilter }: Props) => {
    const selectedFileGroups = useSelector(selectSelectedFileGroups);
    const toggledItemRef = useRef<{ [itemId: string]: boolean }>({});
    const selectedFiles = useSelector(selectAdvancedFiles);
    const dispatch = useDispatch();
    const apiRef = useTreeViewApiRef();

    const selectedFormat = useMemo(() => {
        if (!tabularFilter) return 'all';

        const path : any = [...tabularFilter.selectionPath , 'files_format'];
        const copy = JSON.parse(JSON.stringify(selectedFiles));
        return getWithPath(copy, path);
    }, [selectedFiles, tabularFilter]);

    const flatOptions = useMemo(() => {
        const initItems = [];

        // get the flat version indexed by id and path
        const flatOptions = [];
        const children = [options];
        while(children.length) {
            const child: any = children.shift();

            if (Array.isArray(child)) {
                children.push(...child);
            } else {
                const selectedValue: boolean = isFileSelected(selectedFiles ?? {}, child);
                if (selectedValue) {
                    initItems.push(child.id);
                }

                flatOptions[child.id] = child;
                if (child.children?.length > 0) {
                    children.push(...child.children);
                }
            }
        }

        initItems.length && dispatch(setSelectedFileGroups([...selectedFileGroups, ...initItems]));

        return flatOptions;
    }, [options]);

    const changeFormat = (newFormat: string) => {
        if (!tabularFilter) return;
        const copy = JSON.parse(JSON.stringify(selectedFiles));
        setWithPath(copy, [...tabularFilter.selectionPath , 'files_format'], newFormat);
        dispatch(adwUpdateSelectedFile(copy));
    }

    const getItemDescendantsIds = (item: TreeViewBaseItem) => {
        const ids: string[] = [];
        item.children?.forEach((child) => {
            ids.push(child.id);
            ids.push(...getItemDescendantsIds(child));
        });
        return ids;
    }

    const handleItemSelectionToggle = (
        event: React.SyntheticEvent,
        itemId: any,
        isSelected: boolean,
    ) => {
        toggledItemRef.current[itemId] = isSelected;
    };

    const handleSelectedItemsChange = (
        event: React.SyntheticEvent,
        newSelectedItems: string[],
    ) => {
        const filteredSelectedItems = newSelectedItems.filter((item: any) =>
            flatOptions?.[item]?.type !== 'entity'
        );
        dispatch(setSelectedFileGroups(filteredSelectedItems));
    
        // Select / unselect the children of the toggled item
        const itemsToSelect: string[] = [];
        const itemsToUnSelect: { [itemId: string]: boolean } = {};
        Object.entries(toggledItemRef.current).forEach(([itemId, isSelected]: [any, boolean]) => {
            // @ts-ignore
            const item = apiRef.current!.getItem(itemId);
            if (isSelected) {
                itemsToSelect.push(...getItemDescendantsIds(item));
            } else {
                /* ---
                 * Unselect parent if we unselected the last selected child
                 * --- */

                const clickedItem = flatOptions?.[itemId];
                let currentParentId = clickedItem?.parentId;
                let currentItemId = itemId;

                while(currentParentId) {
                    const parent = flatOptions[currentParentId];
                    if (!parent) break;

                    const childrenIds = parent?.children?.map((el: any) => el.id) || [];
                    const selectedChildrenIds = childrenIds.filter(
                        // Find if there is siblings selected
                        (id: string) => id !== currentItemId && selectedFileGroups.includes(id)
                    );
                    if (selectedChildrenIds.length === 0) {
                        // unselecting parent
                        itemsToUnSelect[parent.id] = true;
                    } else {
                        // we found at least one selected child, aborting
                        break;
                    }
                    currentParentId = parent.parentId;
                    currentItemId = parent.id;
                }
                /* --- */

                getItemDescendantsIds(item).forEach((descendantId) => {
                    itemsToUnSelect[descendantId] = true;
                });
            }
        });

        const newSelectedItemsWithChildren = Array.from(
            new Set(
                [...filteredSelectedItems, ...itemsToSelect].filter(
                    (itemId) => !itemsToUnSelect[itemId],
                ),
            ),
        );

        dispatch(setSelectedFileGroups(newSelectedItemsWithChildren));
        toggledItemRef.current = {};
    };

    return (
        <>
            {tabularFilter ?
                <LassoToggleButtons
                    groupLabel="Tabulated format"
                    size="small"
                    variant="tiny"
                    color="primary"
                    value={selectedFormat}
                    exclusive
                    onChange={(e, newValue) => changeFormat(newValue)}
                    buttons={[
                        { label: 'all', value: 'all' },
                        { label: 'parquet', value: 'parquet,json' },
                        { label: 'BIDS', value: 'tsv,csv,json' },
                    ]}
                /> :
                <></>
            }
            {options.length ?
                <RichTreeView
                    apiRef={apiRef}
                    checkboxSelection
                    multiSelect
                    sx={{
                        "& li": {
                            padding: 0,
                            wordBreak: "break-all",
                            textOverflow: 'ellipsis',
                        },
                        "& li > div": {
                            paddingLeft: 0,
                            background: 'none',
                        },
                        "& li > div:hover": {
                            background: 'none',
                        },
                    }}
                    selectedItems={selectedFileGroups}
                    onSelectedItemsChange={handleSelectedItemsChange}
                    onItemSelectionToggle={handleItemSelectionToggle}
                    items={options}
                    slots={{
                        item: ADWFileTypeOption,
                    }}
                    slotProps={{
                        item: {
                            // @ts-ignore
                            selectedFormat: selectedFormat,
                        },
                    }}
                    itemChildrenIndentation={30}
                    expandedItems={selectedFileGroups}
                /> :
                <Stack
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                    sx={{ height: "300px" }}
                >
                    No Files Available
                </Stack>
            }
        </>
    );
}

export default FileTreeView;